import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { oggiAPI } from '../services/oggiApi';
import { useAuthStore } from '../store/useStore';
import { oraIta } from '../utils/oraItaliana';
import { periodoAvvisi } from '../utils/periodoAvvisi';
import { destinazione, vaiA } from '../utils/destinazione';
/* Il giro e' voluto: schermate.js importa questa pagina, e questa pagina
   chiede alla tabella se l'utente apre una schermata prima di farne un
   collegamento (siApre, la stessa domanda del menu). Si legge solo quando
   la pagina si disegna, mai mentre i moduli si caricano. */
import { schermataDi, siApre } from '../navigazione/schermate';
import { RIMANDO_MINUTI } from './Compiti';
import '../styles/Oggi.css';

/**
 * Oggi — la pagina iniziale del gestionale (stile «Console», fase 2;
 * docs/proposte/26-stile-console.md, mockup «Main»). Sostituisce la
 * dashboard (Alex, 2/10/2026, domande 4 e 7): /dashboard rimanda qui, per
 * i segnalibri e le prove.
 *
 * Per tutti i ruoli, col contenuto per ruolo. CHI VEDE COSA LO DECIDE IL
 * SERVER (GET /api/oggi, backend/controllers/oggiController.js): qui non si
 * filtra niente per ruolo, si disegna quello che arriva.
 *
 *   - LA VISTA, «Tutta la clinica» o «Le mie agende»: le viste di chi
 *     guarda le dice il server (`viste`), e la scelta compare solo se ce
 *     n'e' piu' d'una (un medico, o l'amministrazione collegata a un medico
 *     con un'agenda). La scelta sta nell'indirizzo (?vista=), scritta
 *     sostituendolo come il giorno dell'agenda: una ricarica la ritrova;
 *   - I RIQUADRI: gli appuntamenti di oggi, chi e' in sala d'attesa, le
 *     prestazioni eseguite e non fatturate (solo a chi fattura: la chiave
 *     arriva solo a lui), i controlli automatici. Ogni numero ha una
 *     definizione che il gestionale ha gia' (scritte accanto, nel
 *     controller): nessun numero contato qui;
 *   - L'AGENDA DEL GIORNO, con la linea dell'ora: l'ora italiana del
 *     browser (utils/oraItaliana.js), che si sposta da sola ogni mezzo
 *     minuto. Una riga apre la cartella del paziente (sulla visita, se c'e'),
 *     con la stessa funzione degli avvisi (utils/destinazione.js);
 *   - I COMPITI URGENTI, con «Piu' tardi · 60′»: come nella finestra dei
 *     compiti (Alex, 1/10/2026) rimanda la VISTA, non il compito. Il compito
 *     resta aperto e il server non ne sa niente; qui sparisce per
 *     RIMANDO_MINUTI (lo stesso numero della finestra, pages/Compiti.jsx) e
 *     poi torna. Vale per la persona e per il browser, come la finestra;
 *   - I PROMEMORIA DI OGGI, che stavano nella dashboard (il mockup non li
 *     ha: il posto e' questo, sotto i compiti);
 *   - LA GRIGLIA DEI CONTROLLI: un quadratino per controllo, quelli che
 *     riguardano chi guarda, lo stato di adesso; in testa l'ora dell'ultimo
 *     giro della notte, che apre e chiude i compiti; sotto, quelli che hanno
 *     trovato qualcosa, col numero del catalogo e la schermata dove si
 *     guarda.
 *
 * UNA SEZIONE CHE NON ARRIVA NON SPEGNE LE ALTRE: il server manda null e il
 * nome della sezione in `errori`, e qui quella sezione dice che non
 * risponde mentre le altre si disegnano; un controllo che non risponde e'
 * una cella rossa.
 *
 * Nessuna famiglia di colori nuova (Alex, 2/10/2026, domanda 5): gli stati
 * prendono i colori di tema.css (conferma, attenzione, errore, accento), il
 * quadratino di un appuntamento il colore che la sua agenda ha nei dati.
 *
 * GLI ATTRIBUTI PER LE PROVE (scenario 2601 dei percorsi): data-riquadro,
 * data-vista, data-appuntamento e data-fase, data-ora-adesso, data-sezione,
 * data-compito e data-azione="piu-tardi", data-controllo e data-stato,
 * data-controllo-trovato, data-giro, data-promemoria. Le prove cercano
 * quelli, non le classi del disegno.
 */

/* Ogni quanto si rilegge la giornata: l'orologio degli avvisi del guscio
   (utils/periodoAvvisi.js), un minuto, che le prove accorciano. */
const OGNI = 60 * 1000;
/* Ogni quanto si sposta la linea dell'ora. */
const OROLOGIO = 30 * 1000;
/* Quanti compiti urgenti e promemoria si disegnano: gli altri sono nelle
   loro schermate, a un clic, e il riquadro dice quanti sono. */
const COMPITI_VISIBILI = 6;
const PROMEMORIA_VISIBILI = 8;

/* Le fasi le decide il server (services/salaAttesa.js,
   FASE_DELL_APPUNTAMENTO): qui solo le parole. «Uscito» e «Arrivato» sono
   le parole del pallino dell'agenda; «In sala d'attesa» e' il numero
   accanto ad Agenda. */
export const FASI = {
    atteso: 'Atteso',
    in_sala: 'In sala d\'attesa',
    visita_aperta: 'Visita aperta',
    in_visita: 'In visita',
    uscito: 'Uscito',
    annullato: 'Annullato',
    non_presentato: 'Non presentato',
};

/* Le due viste, con le parole del mockup. */
export const VISTE = { clinica: 'Tutta la clinica', mie: 'Le mie agende' };

const GIORNI = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];

/** «MAR · 06.10.2026» dal giorno della clinica «2026-10-06». */
export function dataInTesta(giorno) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(giorno || ''));
    if (!m) return '';
    const w = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return `${GIORNI[w].toUpperCase()} · ${m[3]}.${m[2]}.${m[1]}`;
}

/** Dove sta la linea dell'ora: prima della prima riga che comincia dopo adesso. */
export function postoDellOra(righe, adesso) {
    const t = adesso.getTime();
    const i = righe.findIndex((r) => new Date(r.inizio).getTime() > t);
    return i < 0 ? righe.length : i;
}

/** «30′», la durata di un appuntamento. */
function durata(r) {
    const m = Math.round((new Date(r.fine) - new Date(r.inizio)) / 60000);
    return Number.isFinite(m) && m > 0 ? `${m}′` : '';
}

/** «GIRO 07:20» se l'ultimo giro e' di oggi, altrimenti col giorno. */
export function testoGiro(g) {
    if (!g || !g.ora) return 'NESSUN GIRO';
    if (g.oggi) return `GIRO ${g.ora}`;
    const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(String(g.giorno || ''));
    return m ? `ULTIMO GIRO ${m[2]}.${m[1]} ${g.ora}` : `ULTIMO GIRO ${g.ora}`;
}

/* Un colore dai dati (agende, stanze) si usa solo se e' un colore: un valore
   strano non entra nello stile. */
const coloreValido = (c) => (/^#[0-9a-f]{3,8}$/i.test(String(c || '')) ? c : null);

/* «Piu' tardi»: dove si ricorda, per persona. Il browser della persona, come
   la finestra dei compiti: una postazione condivisa ha utenti diversi, e il
   rinvio di uno non vale per l'altro. Senza localStorage (finestra privata)
   il compito sparisce finche' la pagina resta aperta. */
const chiaveRimandi = (utente) => `oggi_piu_tardi_${utente}`;
function leggiRimandi(utente) {
    try {
        const v = JSON.parse(localStorage.getItem(chiaveRimandi(utente)) || '{}');
        return v && typeof v === 'object' ? v : {};
    } catch (e) { return {}; }
}
function scriviRimandi(utente, mappa) {
    try { localStorage.setItem(chiaveRimandi(utente), JSON.stringify(mappa)); } catch (e) { /* resta in memoria */ }
}

/** Quanto urge un compito, in parole: il motivo per cui e' qui. */
function perche(c) {
    if (c.scaduto) return 'scaduto';
    if (c.entro_oggi) return 'da fare oggi';
    return 'priorità alta';
}

/* Un riquadro: e' un collegamento solo se chi guarda apre la schermata
   (`apre`, la domanda del menu). Fuori dal componente, perche' un
   componente definito dentro un altro rinasce a ogni giro e perde il
   fuoco. */
const Riquadro = ({ codice, etichetta, valore, sotto, tono, percorso, apre, vai }) => {
    const contenuto = (
        <>
            <span className="oggi-riq-etichetta rm-numeri">{etichetta}</span>
            <span className="oggi-riq-valore rm-numeri">{valore}</span>
            <span className={`oggi-riq-sotto ${tono ? `oggi-tono-${tono}` : ''}`}>
                <span className="oggi-riq-punto" aria-hidden="true" />{sotto}
            </span>
        </>
    );
    return apre(percorso) ? (
        <button type="button" className="oggi-riquadro oggi-riquadro--link" data-riquadro={codice}
                onClick={() => vai(percorso)}>{contenuto}</button>
    ) : (
        <div className="oggi-riquadro" data-riquadro={codice}>{contenuto}</div>
    );
};

/** La linea dell'ora: «ORA 09:41», in ora italiana. */
const LineaOra = ({ adesso }) => (
    <li className="oggi-adesso" data-ora-adesso="1" aria-label={`Adesso sono le ${oraIta(adesso)}`}>
        <span className="oggi-adesso-ora rm-numeri">ORA {oraIta(adesso)}</span>
        <span className="oggi-adesso-linea" />
    </li>
);

const Oggi = () => {
    const navigate = useNavigate();
    const utente = useAuthStore((s) => s.user);
    const [parametri, setParametri] = useSearchParams();
    const vistaChiesta = parametri.get('vista') || '';
    const [dati, setDati] = useState(null);
    const [compiti, setCompiti] = useState({ quanti: 0, compiti: [] });
    const [errore, setErrore] = useState(false);
    const [adesso, setAdesso] = useState(() => new Date());
    const [rimandi, setRimandi] = useState(() => leggiRimandi(utente?.id));
    const vivo = useRef(true);
    const ultima = useRef(0);

    const apre = useCallback((percorso) => {
        const s = percorso ? schermataDi(percorso) : null;
        return !!s && siApre(s, utente);
    }, [utente]);

    /* La giornata e i compiti urgenti: tutte e due a ogni giro. Una che non
       risponde non spegne l'altra; la giornata che non risponde lo dice, e
       i numeri spariscono invece di restare quelli di prima (un numero
       vecchio sembra vero). Una risposta arrivata dopo una piu' nuova (la
       vista cambiata nel frattempo) non si disegna. */
    const carica = useCallback(async () => {
        ultima.current += 1;
        const mia = ultima.current;
        const [g, c] = await Promise.allSettled([
            oggiAPI.giornata(vistaChiesta || undefined), oggiAPI.compitiUrgenti(),
        ]);
        if (!vivo.current || mia !== ultima.current) return;
        if (g.status === 'fulfilled' && g.value?.data?.data) {
            /* Con la vista chiesta: e' su questa che si decide se l'indirizzo
               chiedeva una vista che chi guarda non ha (qui sotto). */
            setDati({ ...g.value.data.data, chiesta: vistaChiesta });
            setErrore(false);
        } else {
            setDati(null);
            setErrore(true);
        }
        if (c.status === 'fulfilled' && c.value?.data?.data) setCompiti(c.value.data.data);
    }, [vistaChiesta]);

    useEffect(() => {
        vivo.current = true;
        carica();
        const giro = setInterval(carica, periodoAvvisi(OGNI));
        const orologio = setInterval(() => setAdesso(new Date()), OROLOGIO);
        return () => { vivo.current = false; clearInterval(giro); clearInterval(orologio); };
    }, [carica]);

    useEffect(() => { setRimandi(leggiRimandi(utente?.id)); }, [utente?.id]);

    /* Una vista chiesta che chi guarda non ha (un indirizzo copiato da un
       collega) vale quella di serie, e l'indirizzo lo dice. */
    useEffect(() => {
        if (dati && vistaChiesta && dati.chiesta === vistaChiesta && dati.vista !== vistaChiesta) {
            setParametri((p) => { const n = new URLSearchParams(p); n.delete('vista'); return n; }, { replace: true });
        }
    }, [dati, vistaChiesta, setParametri]);

    const scegliVista = (v) => {
        if (!dati || v === dati.vista) return;
        setParametri((p) => {
            const n = new URLSearchParams(p);
            if (v === (dati.viste || [])[0]) n.delete('vista'); else n.set('vista', v);
            return n;
        }, { replace: true });
    };

    /* I rimandi scaduti si tolgono, e il compito torna: l'orologio della
       linea dell'ora fa da sveglia (al piu' mezzo minuto di ritardo). */
    const rimandatiAdesso = useMemo(() => {
        const t = adesso.getTime();
        return new Set(Object.entries(rimandi).filter(([, fino]) => Number(fino) > t).map(([id]) => Number(id)));
    }, [rimandi, adesso]);

    const piuTardi = (id) => {
        const t = Date.now();
        const nuovi = Object.fromEntries(Object.entries(leggiRimandi(utente?.id)).filter(([, f]) => Number(f) > t));
        nuovi[id] = t + RIMANDO_MINUTI * 60000;
        scriviRimandi(utente?.id, nuovi);
        setRimandi(nuovi);
    };

    /* Una sezione che il server non e' riuscito a preparare. */
    const caduta = (nome) => !!dati && (dati.errori || []).includes(nome);
    const agenda = dati?.agenda || [];
    const posto = postoDellOra(agenda, adesso);
    const riq = dati?.riquadri;
    const viste = dati?.viste || [];
    const vista = dati?.vista;
    /* I compiti urgenti: il server ne manda al piu' venti, e `quanti` dice
       quanti sono in tutto. Il numero e «e altri» si contano su `quanti`,
       tolti quelli rimandati con «Piu' tardi» (che sono fra i ricevuti:
       si rimanda solo quello che si vede). */
    const ricevuti = compiti.compiti || [];
    const urgenti = ricevuti.filter((c) => !rimandatiAdesso.has(Number(c.id)));
    const rimandatiQui = ricevuti.length - urgenti.length;
    const quantiUrgenti = Math.max(0, Math.max(Number(compiti.quanti) || 0, ricevuti.length) - rimandatiQui);
    const mostrati = Math.min(urgenti.length, COMPITI_VISIBILI);
    const controlli = dati?.controlli || [];
    const daGuardare = controlli.filter((c) => c.stato !== 'ok');
    const promemoria = dati?.promemoria || { quanti: 0, voci: [] };

    const numero = (v) => (v === null || v === undefined ? '–' : Number(v).toLocaleString('it-IT'));
    const NON_DISPONIBILE = 'non risponde: riprovo fra un minuto';

    const nf = riq?.non_fatturate;
    /* Chi non fattura non riceve la chiave: il riquadro non c'e'. */
    const conNonFatturate = !!riq && Object.prototype.hasOwnProperty.call(riq, 'non_fatturate');
    const sottoNonFatturate = () => {
        if (!nf) return caduta('non_fatturate') ? NON_DISPONIBILE : '';
        if (nf.conta_dal) {
            const [y, m, d] = nf.conta_dal.split('-');
            return `si contano dal ${d}/${m}/${y}`;
        }
        if (nf.oltre_soglia === null) return 'giorni del controllo non validi';
        return `${numero(nf.oltre_soglia)} da oltre ${nf.soglia_giorni} giorni`;
    };
    const ctl = riq?.controlli;
    const percorsoControlli = apre('/stato-sistema') ? '/stato-sistema' : '/compiti';
    /* Il riquadro dei controlli: in ordine su quanti, e sotto che cosa c'e'
       da guardare. Zero controlli non e' «tutti in ordine»: e' nessun
       controllo (un account senza medico collegato, per esempio). */
    const rigaControlli = () => {
        if (!ctl) return { valore: '–', sotto: caduta('controlli') ? NON_DISPONIBILE : '', tono: caduta('controlli') ? 'errore' : null };
        if (!ctl.totale) return { valore: '–', sotto: 'nessun controllo ti riguarda', tono: null };
        const problemi = (ctl.da_guardare || 0) + (ctl.soglia_non_valida || 0);
        const errori = ctl.in_errore || 0;
        const parti = [
            problemi ? `${problemi} da guardare` : null,
            errori ? `${errori} non ${errori === 1 ? 'risponde' : 'rispondono'}` : null,
        ].filter(Boolean);
        return {
            valore: `${ctl.in_ordine}/${ctl.totale}`,
            sotto: parti.length ? parti.join(' · ') : 'tutti in ordine',
            tono: errori ? 'errore' : (problemi ? 'attenzione' : 'conferma'),
        };
    };
    const rc = rigaControlli();
    const comeCella = (c) => {
        if (c.stato === 'soglia') return 'soglia non valida';
        if (c.stato === 'errore') return 'non ha risposto';
        return c.n ? String(c.n) : 'in ordine';
    };
    const testoTrovato = (c) => {
        if (c.stato === 'soglia') return 'la soglia nei parametri non è valida';
        if (c.stato === 'errore') return 'non ha risposto, riprovo fra un minuto';
        return null;
    };
    /* «Le mie agende» senza appuntamenti e la clinica senza appuntamenti
       non sono la stessa frase. */
    const vuota = vista === 'mie' ? 'Nessun appuntamento oggi nelle tue agende.' : 'Nessun appuntamento oggi.';

    return (
        <div className="oggi">
            <header className="oggi-testa">
                <div className="oggi-testa-titoli">
                    <span className="oggi-giorno rm-numeri">{dataInTesta(dati?.giorno)}</span>
                    <h1 className="oggi-titolo">Oggi in ambulatorio</h1>
                </div>
                <span className="oggi-spazio" />
                {/* La scelta della vista c'e' solo se chi guarda ne ha due. */}
                {viste.length > 1 && (
                    <div className="oggi-viste" role="group" aria-label="Vista">
                        {viste.map((v) => (
                            <button key={v} type="button" data-vista={v}
                                    className={`oggi-vista ${v === vista ? 'oggi-vista--scelta' : ''}`}
                                    aria-pressed={v === vista} onClick={() => scegliVista(v)}>
                                {VISTE[v] || v}
                            </button>
                        ))}
                    </div>
                )}
            </header>

            {errore && (
                <p className="oggi-errore" role="alert">
                    Non riesco a caricare la giornata di oggi: riprovo da solo fra un minuto.
                </p>
            )}

            <div className="oggi-riquadri">
                <Riquadro apre={apre} vai={navigate} codice="appuntamenti" etichetta="Appuntamenti" percorso="/agenda"
                          valore={numero(riq?.appuntamenti?.n)}
                          tono={caduta('agenda') ? 'errore' : null}
                          sotto={riq?.appuntamenti ? `${numero(riq.appuntamenti.da_arrivare)} ancora da arrivare`
                              : (caduta('agenda') ? NON_DISPONIBILE : '')} />
                <Riquadro apre={apre} vai={navigate} codice="sala" etichetta="In sala d'attesa" percorso="/agenda"
                          valore={numero(riq?.sala?.n)}
                          tono={caduta('agenda') ? 'errore' : (riq?.sala?.n ? 'accento' : null)}
                          sotto={riq?.sala ? (riq.sala.n ? `attesa media ${riq.sala.attesa_media_minuti} min` : 'nessuno in attesa')
                              : (caduta('agenda') ? NON_DISPONIBILE : '')} />
                {conNonFatturate && (
                    <Riquadro apre={apre} vai={navigate} codice="non_fatturate" etichetta="Eseguite, non fatturate" percorso="/fatturazione"
                              valore={numero(nf?.n)}
                              tono={caduta('non_fatturate') ? 'errore' : (nf?.oltre_soglia ? 'attenzione' : null)}
                              sotto={sottoNonFatturate()} />
                )}
                <Riquadro apre={apre} vai={navigate} codice="controlli" etichetta="Controlli" percorso={percorsoControlli}
                          valore={rc.valore} tono={rc.tono} sotto={rc.sotto} />
            </div>

            <div className="oggi-colonne">
                <section className="oggi-sezione" aria-labelledby="oggi-agenda-titolo" data-sezione="agenda">
                    <div className="oggi-sezione-testa">
                        <h2 id="oggi-agenda-titolo">Agenda di oggi</h2>
                        <span className="oggi-conto rm-numeri">
                            {dati && !caduta('agenda') ? `${agenda.length} ${agenda.length === 1 ? 'appuntamento' : 'appuntamenti'}` : ''}
                        </span>
                        <span className="oggi-spazio" />
                        {apre('/agenda') && (
                            <button type="button" className="oggi-link" onClick={() => navigate('/agenda')}>Apri agenda →</button>
                        )}
                    </div>
                    {!dati && !errore && <p className="oggi-vuoto">Caricamento…</p>}
                    {caduta('agenda') && (
                        <p className="oggi-vuoto oggi-sezione-errore">{'Non riesco a leggere l\'agenda di oggi: riprovo da solo fra un minuto.'}</p>
                    )}
                    {dati && !caduta('agenda') && agenda.length === 0 && <p className="oggi-vuoto">{vuota}</p>}
                    {agenda.length > 0 && (
                        <ol className="oggi-righe">
                            {agenda.map((r, i) => (
                                <React.Fragment key={r.id}>
                                    {i === posto && <LineaOra adesso={adesso} />}
                                    <li className={`oggi-riga oggi-fase-${r.fase}`} data-appuntamento={r.id} data-fase={r.fase}>
                                        {/* La cartella del paziente, sulla visita se c'e':
                                            la stessa strada di un avviso (destinazione). */}
                                        <button type="button" className="oggi-riga-corpo" disabled={!r.paziente_id}
                                                onClick={() => vaiA(navigate, {
                                                    paziente_id: r.paziente_id,
                                                    riferimento_tipo: r.visita_id ? 'visita' : null,
                                                    riferimento_id: r.visita_id || null,
                                                })}>
                                            <span className="oggi-riga-ora">
                                                <span className="oggi-ora rm-numeri">{oraIta(r.inizio)}</span>
                                                <span className="oggi-durata rm-numeri">{durata(r)}</span>
                                            </span>
                                            <span className="oggi-riga-testo">
                                                <span className="oggi-paziente rm-tronca" title={r.paziente || ''}>{r.paziente || '—'}</span>
                                                <span className="oggi-cosa rm-tronca"
                                                      title={[r.cosa, r.agenda_nome, r.stanza_nome].filter(Boolean).join(' · ')}>
                                                    <span className="oggi-colore" aria-hidden="true"
                                                          style={coloreValido(r.agenda_colore) ? { background: r.agenda_colore } : undefined} />
                                                    {[r.cosa, r.stanza_nome || r.agenda_nome].filter(Boolean).join(' · ')}
                                                </span>
                                            </span>
                                            <span className={`oggi-stato oggi-stato--${r.fase}`}>{FASI[r.fase] || r.fase}</span>
                                        </button>
                                    </li>
                                </React.Fragment>
                            ))}
                            {posto === agenda.length && <LineaOra adesso={adesso} />}
                        </ol>
                    )}
                </section>

                <div className="oggi-destra">
                    <section className="oggi-sezione" aria-labelledby="oggi-compiti-titolo" data-sezione="compiti">
                        <div className="oggi-sezione-testa">
                            <h2 id="oggi-compiti-titolo">Compiti urgenti</h2>
                            {quantiUrgenti > 0 && (
                                <span className="oggi-chip rm-numeri">{quantiUrgenti}</span>
                            )}
                            <span className="oggi-spazio" />
                            <button type="button" className="oggi-link" onClick={() => navigate('/compiti')}>Compiti →</button>
                        </div>
                        {urgenti.length === 0 ? (
                            <p className="oggi-vuoto">
                                {/* Rimandati quelli arrivati, ma il server ne ha altri:
                                    non sono «tutti». */}
                                {quantiUrgenti > 0 ? 'Hai rimandato quelli di questa pagina: tornano da soli fra poco.'
                                    : (rimandatiQui ? 'Li hai rimandati tutti: tornano da soli fra poco.' : 'Nessun compito urgente.')}
                            </p>
                        ) : (
                            <ul className="oggi-compiti">
                                {urgenti.slice(0, COMPITI_VISIBILI).map((c) => {
                                    const dove = destinazione(c);
                                    return (
                                        <li key={c.id} className="oggi-compito" data-compito={c.id}>
                                            <span className="oggi-compito-testo">
                                                <span className="oggi-compito-titolo">{c.titolo}</span>
                                                <span className="oggi-compito-meta">
                                                    {[c.paziente_cognome ? `${c.paziente_cognome} ${c.paziente_nome || ''}`.trim() : null, perche(c)]
                                                        .filter(Boolean).join(' · ')}
                                                </span>
                                            </span>
                                            <span className="oggi-compito-azioni">
                                                <button type="button" className="oggi-btn oggi-btn--primario"
                                                        onClick={() => (dove ? vaiA(navigate, c) : navigate('/compiti'))}>
                                                    {dove ? 'Apri' : 'Apri i compiti'}
                                                </button>
                                                <button type="button" className="oggi-btn" data-azione="piu-tardi"
                                                        title="Lo nasconde da qui per un'ora: il compito resta aperto"
                                                        onClick={() => piuTardi(c.id)}>
                                                    Più tardi · {RIMANDO_MINUTI}′
                                                </button>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {quantiUrgenti > mostrati && (
                            <p className="oggi-altri">e altri {quantiUrgenti - mostrati} nella pagina Compiti</p>
                        )}
                    </section>

                    <section className="oggi-sezione" aria-labelledby="oggi-promemoria-titolo" data-sezione="promemoria">
                        <div className="oggi-sezione-testa">
                            <h2 id="oggi-promemoria-titolo">Promemoria di oggi</h2>
                            <span className="oggi-spazio" />
                            {apre('/promemoria') && (
                                <button type="button" className="oggi-link" onClick={() => navigate('/promemoria')}>Promemoria →</button>
                            )}
                        </div>
                        {caduta('promemoria') && (
                            <p className="oggi-vuoto oggi-sezione-errore">Non riesco a leggere i promemoria di oggi: riprovo da solo fra un minuto.</p>
                        )}
                        {!caduta('promemoria') && promemoria.voci.length === 0 && (
                            <p className="oggi-vuoto">{dati ? 'Nessun promemoria per oggi.' : ''}</p>
                        )}
                        {!caduta('promemoria') && promemoria.voci.length > 0 && (
                            <ul className="oggi-promemoria">
                                {promemoria.voci.slice(0, PROMEMORIA_VISIBILI).map((a) => (
                                    <li key={a.id} data-promemoria={a.id}>
                                        <button type="button" className="oggi-prom-voce" onClick={() => vaiA(navigate, a)}
                                                disabled={!destinazione(a)}>
                                            <span className="oggi-ora rm-numeri">{a.ora}</span>
                                            <span className="oggi-prom-testo">
                                                <span className="oggi-compito-titolo rm-tronca" title={a.titolo}>{a.titolo}</span>
                                                {a.paziente && <span className="oggi-compito-meta rm-tronca">{a.paziente}</span>}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {promemoria.quanti > PROMEMORIA_VISIBILI && (
                            <p className="oggi-altri">e altri {promemoria.quanti - PROMEMORIA_VISIBILI} nella schermata Promemoria</p>
                        )}
                    </section>

                    <section className="oggi-sezione oggi-sezione--controlli" aria-labelledby="oggi-controlli-titolo" data-sezione="controlli">
                        <div className="oggi-sezione-testa oggi-sezione-testa--piana">
                            <h2 id="oggi-controlli-titolo">Controlli automatici</h2>
                            <span className="oggi-spazio" />
                            {dati && !caduta('giro') && (
                                <span className={`oggi-conto rm-numeri ${dati.giro?.esito === 'fallito' ? 'oggi-tono-errore' : ''}`} data-giro="1"
                                      title="L'ultimo giro della notte, che apre e chiude i compiti dei controlli. I quadratini dicono come stanno adesso.">
                                    {testoGiro(dati.giro)}{dati.giro?.esito === 'fallito' ? ' · NON RIUSCITO' : ''}
                                </span>
                            )}
                        </div>
                        {caduta('controlli') && (
                            <p className="oggi-vuoto oggi-sezione-errore">Non riesco a leggere i controlli automatici: riprovo da solo fra un minuto.</p>
                        )}
                        {!caduta('controlli') && controlli.length === 0 && (
                            <p className="oggi-vuoto">{dati ? 'Nessun controllo automatico ti riguarda.' : ''}</p>
                        )}
                        {!caduta('controlli') && controlli.length > 0 && (
                            <div className="oggi-griglia" role="list" aria-label="Controlli automatici">
                                {controlli.map((c) => (
                                    <span key={c.codice} role="listitem" className={`oggi-cella oggi-cella--${c.stato}`}
                                          data-controllo={c.codice} data-stato={c.stato}
                                          title={`#${c.id} ${c.nome}: ${comeCella(c)}`}
                                          aria-label={`${c.nome}: ${comeCella(c)}`} />
                                ))}
                            </div>
                        )}
                        {daGuardare.length > 0 && (
                            <ul className="oggi-trovati">
                                {daGuardare.map((c) => (
                                    <li key={c.codice} className={`oggi-trovato ${c.stato === 'trovato' ? '' : 'oggi-trovato--errore'}`}
                                        data-controllo-trovato={c.codice}>
                                        <span className="oggi-trovato-numero rm-numeri">#{c.id}</span>
                                        <span className="oggi-trovato-testo">
                                            {c.nome}: {testoTrovato(c) || <b className="rm-numeri">{c.n}</b>}
                                        </span>
                                        {apre(c.schermata) ? (
                                            <button type="button" className="oggi-link oggi-link--tono" onClick={() => navigate(c.schermata)}>Vedi</button>
                                        ) : (
                                            <button type="button" className="oggi-link oggi-link--tono" onClick={() => navigate('/compiti')}>Compiti</button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Oggi;
